import type { IsoDate } from './calendar';
import type { Rate } from './invoice';

/**
 * The GST rates that exist on a date.
 *
 * From 22 September 2025 ("GST 2.0", Notification 9/2025-Central Tax (Rate), Schedules I–VII) goods are
 * taxed at 5% or 18%, with 40% for a short list (sugared drinks, motorcycles over 350 cc, bigger cars…),
 * 3% for gold, silver and jewellery, and 0.25% / 1.5% for diamonds and precious stones. 28% stayed only
 * for tobacco and pan masala, until those moved to 40% on 1 February 2026 (19/2025-CT(Rate)). 12% and
 * 6% are left only for bricks and roofing tiles (14/2025 and 02/2022-CT(Rate)). Exempt goods are 0%;
 * supplies to merchant exporters 0.1%. Services keep their special rates: 1.5% and 7.5% for housing,
 * 12% for old housing projects.
 */
export type SupplyNature = 'goods' | 'services';

interface RateSchedule {
  from: IsoDate;
  goods: readonly Rate[];
  services: readonly Rate[];
  /** The ones most sales use, offered first. */
  common: readonly Rate[];
}

const SCHEDULES: readonly RateSchedule[] = [
  {
    from: '2017-07-01',
    goods: [0, 0.1, 0.25, 1.5, 3, 5, 6, 12, 18, 28],
    services: [0, 0.25, 1, 1.5, 3, 5, 6, 7.5, 12, 18, 28],
    common: [0, 5, 12, 18, 28],
  },
  {
    from: '2025-09-22',
    goods: [0, 0.1, 0.25, 1.5, 3, 5, 6, 12, 18, 28, 40],
    services: [0, 0.25, 1, 1.5, 3, 5, 6, 7.5, 12, 18, 40],
    common: [0, 0.25, 3, 5, 18, 40],
  },
  {
    from: '2026-02-01',
    goods: [0, 0.1, 0.25, 1.5, 3, 5, 6, 12, 18, 40],
    services: [0, 0.25, 1, 1.5, 3, 5, 6, 7.5, 12, 18, 40],
    common: [0, 0.25, 3, 5, 18, 40],
  },
];

/** The day GST 2.0's rates began. */
export const GST_2_FROM: IsoDate = '2025-09-22';

function scheduleOn(date: IsoDate): RateSchedule {
  let found = SCHEDULES[0] as RateSchedule;
  for (const schedule of SCHEDULES) if (schedule.from <= date) found = schedule;
  return found;
}

/** Every GST rate in use on a date for goods (the default) or services. */
export const allowedRates = (date: IsoDate, nature: SupplyNature = 'goods'): readonly Rate[] =>
  scheduleOn(date)[nature];

/** The rates most sales use on a date, for pickers. */
export const commonRates = (date: IsoDate): readonly Rate[] => scheduleOn(date).common;

/** Rates that, from GST 2.0, only a few goods still use: which HSN chapters, and why the rest don't. */
const NARROW_RATES: readonly {
  rate: Rate;
  from: IsoDate;
  to?: IsoDate;
  chapters: readonly string[];
  why: RetiredRate;
}[] = [
  // Bricks and roofing tiles (chapters 68 and 69).
  { rate: 12, from: GST_2_FROM, chapters: ['68', '69'], why: 'twelve' },
  { rate: 6, from: GST_2_FROM, chapters: ['68', '69'], why: 'six' },
  // Tobacco and pan masala, until they moved to 40%.
  { rate: 28, from: GST_2_FROM, to: '2026-01-31', chapters: ['24', '2106'], why: 'twentyEight' },
];

export type RetiredRate = 'twelve' | 'six' | 'twentyEight';

/**
 * Why a rate that goods used before isn't right for this one on this date: 12% and 28% ended for all
 * but a few goods on 22 September 2025 (most 12% goods moved to 5%, most 28% goods to 18%), and 6% is
 * only for bricks. Null when the rate is fine, or when the reason is something else. Without an HSN
 * code the rate is given the benefit of the doubt.
 */
export function retiredRate(
  rate: Rate,
  date: IsoDate,
  nature: SupplyNature = 'goods',
  hsn?: string,
): RetiredRate | null {
  if (nature !== 'goods' || date < GST_2_FROM) return null;
  if ((rate === 12 || rate === 28) && !allowedRates(date, nature).includes(rate))
    return rate === 12 ? 'twelve' : 'twentyEight';
  for (const narrow of NARROW_RATES) {
    if (narrow.rate !== rate || date < narrow.from || (narrow.to && date > narrow.to)) continue;
    if (hsn && !narrow.chapters.some((c) => hsn.startsWith(c))) return narrow.why;
  }
  return null;
}

/** Whether a rate can be charged on a date, for an HSN code when it's known. */
export function isAllowedRate(rate: Rate, date: IsoDate, nature: SupplyNature = 'goods', hsn?: string): boolean {
  return allowedRates(date, nature).includes(rate) && retiredRate(rate, date, nature, hsn) === null;
}
